// PKCE (RFC 7636) helpers for the Hub sign-in redirect.
type CryptoLike = Pick<Crypto, 'getRandomValues' | 'subtle'>;

const VERIFIER_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
export const CODE_VERIFIER_LENGTH = 64;

export const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const generateCodeVerifier = (
  cryptoImpl: CryptoLike = window.crypto,
  length: number = CODE_VERIFIER_LENGTH,
): string => {
  // Bytes past the last full multiple of the charset are dropped to avoid modulo bias
  const limit = 256 - (256 % VERIFIER_CHARSET.length);
  let verifier = '';
  while (verifier.length < length) {
    const bytes = cryptoImpl.getRandomValues(new Uint8Array(length));
    for (let i = 0; i < bytes.length && verifier.length < length; i += 1) {
      if (bytes[i] < limit) {
        verifier += VERIFIER_CHARSET[bytes[i] % VERIFIER_CHARSET.length];
      }
    }
  }
  return verifier;
};

export const computeCodeChallenge = async (
  verifier: string,
  cryptoImpl: CryptoLike = window.crypto,
): Promise<string> => {
  // The verifier is ASCII by construction, so char codes are its UTF-8 bytes
  const data = Uint8Array.from(verifier, (char) => char.charCodeAt(0));
  const digest = await cryptoImpl.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

export const generateState = (cryptoImpl: CryptoLike = window.crypto): string =>
  base64UrlEncode(cryptoImpl.getRandomValues(new Uint8Array(32)));
